/**
 * Every aiWARE data registry Redact depends on — the list the /admin/dependencies smoke
 * check walks.
 *
 * Source of truth is the code: an entry per registry the app resolves (via
 * `getLatestSchemaId` or a module's own resolver), under the config key and fallbacks
 * that resolver uses. Most of them are installed by the "Redact Schemas" aiWARE package
 * (us-1 package db81abab-fbc3-4adf-af94-bcebdf9e2bee, v6.0.0: ten schemas).
 * `notInSchemasPackage` marks the ones that package does NOT install — lexicons,
 * watermarks and status history as of v6.0.0 — so an environment built from the package
 * alone fails them, and the page says why. Adding a registry to the app means adding it
 * here; `package.test.ts` pins the package half.
 *
 * Each entry names the `window.config` key the app reads, the legacy key it falls back
 * to, the built-in id it falls back to after that (so the check tests the registry the
 * app would really use), and the feature flag that gates the feature, if any.
 *
 * Deliberately absent:
 * - `caseNotificationRegistryId` / `cbsaCase*RegistryId` — deprecated cbsa-app only, and
 *   not in the package (the configured id does not exist on us-1). `caseRegistryId` still
 *   falls back to `cbsaCaseRegistryId`, which the case entry honours.
 * - `redactionStatusesSchemaId` — a pinned schema, not a registry; checked separately by the
 *   saga only when an environment sets it.
 */
import {
  FALLBACK_DOCUMENT_COMMENTS_REGISTRY_ID,
  FALLBACK_REDACTION_STATUSES_REGISTRY_ID,
} from '@helpers/constants';
import { DEFAULT_TDO_STATUS_UPDATES_REGISTRY_ID } from '@common-modules/statusHistory/registryId';
import { LEXICON_REGISTRY_ID_DEFAULT } from '@redact-modules/lexicons/registry';
import { DEFAULT_WATERMARK_REGISTRY_ID } from '@redact-modules/watermarks/services';

type Config = Window['config'];

/** Config keys whose value is a data registry id. */
export type RegistryConfigKey =
  | 'tdoRedactFileStatusRegistryId'
  | 'redactNotificationRegistryId'
  | 'caseRegistryId'
  | 'redactMediaCommentRegistryId'
  | 'redactDocumentCommentsRegistryId'
  | 'redactionStatusesRegistryId'
  | 'redactionCodesRegistryId'
  | 'tdoLockRegistryId'
  | 'settingsProfileRegistryId'
  | 'eventSchemaId'
  | 'lexiconRegistryId'
  | 'watermarkRegistryId'
  | 'tdoStatusHistoryRegistryId';

export type FeatureFlagKey = keyof NonNullable<Config['featureFlags']>;

export interface RegistryDependency {
  readonly configKey: RegistryConfigKey;
  /** Another config key the app reads when `configKey` is unset. */
  readonly fallbackConfigKey?: keyof Config;
  /** Id the app hard-codes when no config key is set. */
  readonly defaultId?: string;
  /** Feature flag that must be on for the app to use this registry. */
  readonly featureFlag?: FeatureFlagKey;
  /** An explicit `''` for the config key turns the feature off (no default applies). */
  readonly emptyStringDisables?: true;
  /** The Redact Schemas package (v6.0.0) does not install this registry. */
  readonly notInSchemasPackage?: true;
}

export const REGISTRY_DEPENDENCIES: ReadonlyArray<RegistryDependency> = [
  // The legacy "…SchemaId" keys hold the same registry ids (see helpers/registryConfig).
  {
    configKey: 'tdoRedactFileStatusRegistryId',
    fallbackConfigKey: 'tdoRedactStateSchemaId',
  },
  {
    configKey: 'redactNotificationRegistryId',
    fallbackConfigKey: 'redactNotificationSchemaId',
  },
  { configKey: 'caseRegistryId', fallbackConfigKey: 'cbsaCaseRegistryId' },
  { configKey: 'redactMediaCommentRegistryId' },
  {
    configKey: 'redactDocumentCommentsRegistryId',
    defaultId: FALLBACK_DOCUMENT_COMMENTS_REGISTRY_ID,
  },
  {
    configKey: 'redactionStatusesRegistryId',
    defaultId: FALLBACK_REDACTION_STATUSES_REGISTRY_ID,
  },
  { configKey: 'redactionCodesRegistryId', featureFlag: 'redactionCodes' },
  { configKey: 'tdoLockRegistryId', featureFlag: 'tdoLock' },
  { configKey: 'settingsProfileRegistryId' },
  // Audit log registry ("Veritone Redact Audit Log"). Nothing resolves its schema —
  // emitAuditEvent takes none — but the key's presence gates audit emission.
  { configKey: 'eventSchemaId' },
  {
    configKey: 'lexiconRegistryId',
    defaultId: LEXICON_REGISTRY_ID_DEFAULT,
    notInSchemasPackage: true,
  },
  {
    configKey: 'watermarkRegistryId',
    defaultId: DEFAULT_WATERMARK_REGISTRY_ID,
    featureFlag: 'enableWatermarks',
    notInSchemasPackage: true,
  },
  {
    configKey: 'tdoStatusHistoryRegistryId',
    defaultId: DEFAULT_TDO_STATUS_UPDATES_REGISTRY_ID,
    // resolveTdoStatusUpdatesRegistryId: '' disables status history in that env.
    emptyStringDisables: true,
    notInSchemasPackage: true,
  },
];

export type IdSource = 'config' | 'fallbackConfig' | 'default';

/**
 * The registry id the app would use for `dep`, and where it came from — mirrors the
 * `lookupLatest…SchemaId` helpers: config key, then fallback key, then built-in default.
 */
export const resolveRegistryId = (
  dep: RegistryDependency,
  config: Partial<Config>
): { readonly id?: string; readonly source?: IdSource } => {
  const fromConfig = config[dep.configKey];
  if (typeof fromConfig === 'string' && fromConfig) {
    return { id: fromConfig, source: 'config' };
  }
  const fromFallback = dep.fallbackConfigKey
    ? config[dep.fallbackConfigKey]
    : undefined;
  if (typeof fromFallback === 'string' && fromFallback) {
    return { id: fromFallback, source: 'fallbackConfig' };
  }
  if (dep.defaultId) {
    return { id: dep.defaultId, source: 'default' };
  }
  return {};
};
